// ============================================================
// attack/shot - resume an attack from a successful hit.
//
// Shared by the normal hit path, a re-roll that hits, a Furious Assault
// bonus attack, an auto-hitting template and a missed blast's scatter.
// Two halves: `aimShot` decides everything read off the ROLL - DoS, hit
// count, penetration, the template's sizes - once for the whole volley;
// `landShot` decides who it lands on and resolves each of them
// (`attack/victim`).
//
// ROLL ORDER CONTRACT: the Haywire d10, then the template's dice-valued
// sizes, then the caller's victim sweep, then one d10 per hit for a
// suppression burst, then each victim in order, then every spawned one.
// ============================================================
import { areaOf, resolveAreaSizes, describeRolledSizes, type ResolvedArea } from '../../engine/areaShapes';
import { beatKindFacts, type BeatKindFacts } from './beatFacts';
import { spawnersOf } from '../../engine/battlefield/spawnedArea';
import { exposedDos, onHitExposure, isExposureCode } from '../../engine/exposures';
import { assignHitsRandomly } from '../../engine/suppression';
import type { CountdownResult } from '../../engine/countdown';
import type { ModifierDetail } from '../../engine/structuredNarrative';
import type { ScatterResult } from '../../engine/scatter';
import {
  rollDie,
  hasWS,
  getWSP,
  accurateBonusDice,
  calcDoS,
  calcTotalHits,
  calcEffectivePen,
  applyUnnaturalDos,
} from '../../engine/index';
import { hitMode } from '../../engine/attackCalc';
import { attackingMagnitude } from '../../engine/bodyShape';
import type { Slot, Tab } from '../../types';
import { actorReader, aimedBearer, type AttackContext } from './context';
import { arcNote, handlingNotes, isLeapingArc, weaponNotes, type ShotSituation } from './shotNotes';
import { resolveAgainstVictim, type Volley } from './victim';
import type { AttackVictim } from './types';

/** A hit, as the path that rolled it hands it over. */
export interface HitInput {
  attackRoll: number;
  modifiedSkill: number;
  slot: Slot;
  /** The attack's special notes so far - APPENDED to, by reference. */
  notes: string[];
  overheatDmg: number;
  cdResult: CountdownResult | null;
  tabId: string;
  tab: Tab;
  /** Phase 7: a Furious Assault swing - single mode, no Hammer Blow, no further bonus. */
  isBonusAttack?: boolean;
  /**
   * The swing was an All Out Attack, as the re-roll prompt recorded it (2026-10-22): the swing spends the toggle
   * before the re-roll is answered, and a re-rolled hit is still that attack (Hammer Blow, Furious Assault). Absent:
   * the toggle as it stands.
   */
  allOut?: boolean;
  skillBreakdown?: ModifierDetail[];
  /**
   * Where a missed blast went, if it went anywhere. Non-null means this
   * detonation is a mishap rather than a shot: the caller (which owns the
   * map) moves the template.
   */
  scatter?: ScatterResult | null;
}

/** The hit with everything the roll decided, shared by every victim of the volley. */
export interface Shot extends HitInput {
  sp: string[];
  isMelee: boolean;
  /** Haywire (v8.198.8): ONE field per attack - every victim of a haywire blast suffers the same severity. */
  haywireRoll: number;
  dos: number;
  effectiveMode: string;
  /** The attack's kind and rate for its beat (plan design-gaps K1), read once here for both payloads. */
  beatKind: BeatKindFacts;
  numHits: number;
  pen: number;
  /** THE TEMPLATE'S SIZES, ROLLED ONCE (v8.372.4) - every victim of one shot sees one shape. */
  rolledArea: ResolvedArea;
  accurateBonus: number;
  regenHealed: number;
  /** Hammer Blow's +2 damage and +2 penetration (All-Out melee, not on a bonus attack), else 0. */
  hammerBlow: number;
  furAssaultEligible: boolean;
  /** What the attacker wears, for what THEIR fire does to a victim (a `pin` axis, v8.368.5). */
  attackerExposures: string[];
}

/**
 * The attack's degrees of success. Unnatural DoS (house rule) inflates them
 * off the governing characteristic's ×N; a `dos` exposure on the aimed
 * target (v8.350.0) shifts them - here, where they are first known.
 */
function shotDos(ctx: AttackContext, input: HitInput, isMelee: boolean): number {
  const unnatural = applyUnnaturalDos(
    calcDoS(input.modifiedSkill, input.attackRoll),
    input.attackRoll,
    input.modifiedSkill,
    ctx.attackerCharStats?.skillCharValue ?? 0,
    ctx.attackerCharStats?.skillUnnaturalMultiplier ?? 1,
    ctx.unnaturalDosMode,
  );
  const dos = exposedDos(unnatural.dos, ctx.currentTab?.target ?? { conditions: [] }, actorReader(ctx, isMelee));
  if (unnatural.note) input.notes.push(unnatural.note);
  return dos;
}

/** The volley's hit count. What the aimed target is exposed to shapes it for every victim (v8.368.1). */
function shotHits(ctx: AttackContext, input: HitInput, dos: number, sp: string[], isMelee: boolean) {
  const { effectiveMode, rof } = hitMode(ctx.fireMode, input.slot, isMelee, !!input.isBonusAttack);
  const hitCalc = calcTotalHits(dos, effectiveMode, rof, sp, ctx.rangeBand, {
    isMelee,
    isHorde: ctx.currentTab?.target?.horde?.isHorde || false,
    suppressing: ctx.suppressing,
    shift: onHitExposure(aimedBearer(ctx), actorReader(ctx, isMelee)).hits,
    // A horde attacking scores +1 hit per 10 magnitude (ruling 2026-10-09); the forecasts read the same.
    attackerMagnitude: attackingMagnitude(ctx.actorConditionContext),
  });
  input.notes.push(...hitCalc.notes);
  return { effectiveMode, numHits: hitCalc.hits };
}

/** The template's sizes, rolled once, and the lines that describe them. */
function rollTemplate(sp: string[], slot: Slot, notes: string[]): ResolvedArea {
  const rolledArea = resolveAreaSizes(areaOf(slot), sp);
  if (rolledArea.rolled.length) notes.push('Area: ' + describeRolledSizes(rolledArea));
  const arc = arcNote(rolledArea);
  if (arc) notes.push(arc);
  return rolledArea;
}

/** Everything read off the roll, in dice order - see the file header. */
function aimShot(ctx: AttackContext, input: HitInput): Shot {
  const sp = input.slot.specialRules || [];
  const isMelee = input.slot.weaponType === 'melee';
  const haywireRoll = hasWS(sp, 'Haywire') ? rollDie(10) + getWSP(sp, 'Haywire') : 0;
  const dos = shotDos(ctx, input, isMelee);
  const { effectiveMode, numHits } = shotHits(ctx, input, dos, sp, isMelee);
  const read = { sp, isMelee, dos, effectiveMode };
  return {
    ...input,
    ...read,
    // The kind and rate for the beat, read from the shot's own effective mode (a bonus attack's single included).
    beatKind: beatKindFacts(ctx, isMelee, effectiveMode),
    haywireRoll,
    numHits,
    ...penAndTemplate(ctx, input, read),
    ...allOutRiders(ctx, input, isMelee),
  };
}

/** What the roll has decided by the time the penetration is read. */
interface ShotRead {
  sp: string[];
  isMelee: boolean;
  dos: number;
  effectiveMode: string;
}

/** The penetration, the weapon's lines with the template rolled between them, and Accurate's bonus. */
function penAndTemplate(ctx: AttackContext, input: HitInput, { sp, isMelee, dos, effectiveMode }: ShotRead) {
  const penCalc = calcEffectivePen(input.slot.penetration, sp, dos, ctx.rangeBand);
  input.notes.push(...penCalc.notes);
  const situation: ShotSituation = {
    pen: penCalc.pen,
    rangeBand: ctx.rangeBand,
    isMelee,
    indirectNoLoS: ctx.indirectNoLoS,
    psyRating: input.slot.psyRating || 0,
  };
  input.notes.push(...weaponNotes(sp, situation));
  const rolledArea = rollTemplate(sp, input.slot, input.notes);
  input.notes.push(...handlingNotes(sp, situation));
  // Accurate's bonus dice require the shot to have been Aimed (official gating, v8.197.14).
  const accurateBonus = accurateBonusDice(sp, effectiveMode, dos, !!(ctx.isAimHalf || ctx.isAimFull));
  if (accurateBonus > 0) input.notes.push('Accurate: +' + accurateBonus + 'd10 damage');
  return { pen: penCalc.pen, rolledArea, accurateBonus };
}

/** Hammer Blow and Furious Assault ride an All-Out melee swing - never a bonus attack (Phase 7). */
function allOutRiders(ctx: AttackContext, input: HitInput, isMelee: boolean) {
  const allOutMelee = isMelee && (input.allOut ?? ctx.isAllOutAttack) && !input.isBonusAttack;
  const actor = ctx.actorConditionContext;
  return {
    regenHealed: input.cdResult?.regenHealed || 0,
    hammerBlow: allOutMelee && ctx.hasHammerBlow ? 2 : 0,
    furAssaultEligible: allOutMelee && (ctx.attackerCharStats?.hasFuriousAssault ?? false),
    attackerExposures: [
      ...((actor?.exposures as string[] | undefined) ?? []),
      ...((actor?.conditions as string[] | undefined) ?? []).filter(isExposureCode),
    ],
  };
}

/**
 * SUPPRESSION SCATTERS ITS HITS (v8.307.0). The burst is not aimed at
 * anybody, so each hit picks a victim at random - three hits into four
 * people may all land on one of them, which is the point.
 */
function scatterSuppression(ctx: AttackContext, shot: Shot, victims: AttackVictim[]): AttackVictim[] {
  if (!ctx.suppressing || victims.length === 0) return victims;
  const counts = assignHitsRandomly(
    shot.numHits,
    victims.map((v) => v.tabId),
  );
  const shared = victims.map((v) => ({ ...v, hitCount: counts[v.tabId] ?? 0 }));
  const spread = shared.map((v) => (v.target?.name || v.tabId) + ' ' + (v.hitCount ?? 0)).join(', ');
  const n = shot.numHits;
  shot.notes.push('Suppressing Fire scatters ' + n + ' hit' + (n === 1 ? '' : 's') + ': ' + spread);
  return shared;
}

/**
 * A rule that silently does nothing reads as broken (first playtest,
 * 2026-08-31): an arc with no echo, and a secondary with no map, say why.
 */
function reachNotes(ctx: AttackContext, shot: Shot, mapAnswered: boolean, victims: AttackVictim[]): void {
  const area = shot.rolledArea;
  if (isLeapingArc(area) && !victims.some((v) => (v.leap ?? 0) > 0)) {
    shot.notes.push(
      'Arc: nowhere to leap - no other combatant within ' + area.reach + 'm of the target on the battlefield',
    );
  }
  if (area.spawn && (!mapAnswered || !ctx.resolveSpawned)) {
    shot.notes.push('Spawned ' + area.spawn.shape + ': needs the battlefield - nobody else is caught');
  }
}

/** The template landed where nobody was standing. */
function catchesNobody(ctx: AttackContext, shot: Shot): void {
  ctx.addNarrative(shot.tabId, {
    type: 'info',
    text: shot.scatter ? 'The blast scatters wide and catches nobody.' : 'The blast catches nobody.',
    timestamp: Date.now(),
  });
  ctx.historyTrack.current = true;
}

/** Resolve each victim in turn; returns the ids the template caught. */
function strikeAll(ctx: AttackContext, shot: Shot, victims: AttackVictim[], volley: Volley): string[] {
  const caughtIds: string[] = [];
  for (const victim of victims) {
    if (resolveAgainstVictim(ctx, shot, victim, volley)) caughtIds.push(victim.tabId);
  }
  return caughtIds;
}

/**
 * THE SECOND PHASE (v8.373.2). Who spawns depends on the avoid tests just
 * rolled, so the map is asked again, now. Every spawned victim resolves
 * AFTER every primary one, in spawner order - this only APPENDS dice.
 */
function strikeSpawned(
  ctx: AttackContext,
  shot: Shot,
  free: AttackVictim[],
  caughtIds: string[],
  volleyId: string | null,
) {
  const spawn = shot.rolledArea.spawn;
  if (!spawn || !ctx.resolveSpawned) return;
  const steppedClear = new Set(free.filter((v) => !caughtIds.includes(v.tabId)).map((v) => v.tabId));
  const spawners = spawnersOf(
    free.map((v) => ({ id: v.tabId, leap: v.leap })),
    steppedClear,
    shot.tabId,
    spawn.spawnedBy,
  );
  // Someone who stepped clear was never struck: a neighbour's template may still reach them.
  const spawned = (spawners.length ? ctx.resolveSpawned(shot.slot, spawners, spawn, caughtIds) : null) ?? [];
  // A copy of a scattered primary waits for its slide on the map too (T2, the Track T review).
  const freeSpawned = spawned
    .filter((v) => !ctx.isDefencePending?.(v.tabId))
    .map((v) => (shot.scatter ? lateCopy(v) : v));
  // The spawned lines know the whole count; the primaries could not (see `volleyHeading`).
  const volley = { id: volleyId, size: free.length + freeSpawned.length };
  for (const victim of freeSpawned) resolveAgainstVictim(ctx, shot, victim, volley);
}

/** A spawned victim of a scattered blast, its template marked to wait for the primary's slide. */
const lateCopy = (v: AttackVictim): AttackVictim => (v.area ? { ...v, area: { ...v.area, late: true } } : v);

/**
 * A victim already answering another attack is skipped rather than
 * overwritten: the defence collection is keyed by combatant, so a second
 * document would silently replace the first and lose that attack.
 */
function freeVictims(ctx: AttackContext, shot: Shot, victims: AttackVictim[]): AttackVictim[] {
  const free = victims.filter((v) => v.tabId === shot.tabId || !ctx.isDefencePending?.(v.tabId));
  if (free.length < victims.length) {
    shot.notes.push('Some in the area were already answering another attack and were skipped');
  }
  return free;
}

/**
 * WHO THIS SHOT LANDS ON, and each of them resolved. One name, unless the
 * caller - which owns the map - says the shape caught more.
 *
 * NULL AND EMPTY MEAN DIFFERENT THINGS. Null is "not an area attack, or no
 * map to ask" and falls back to the combatant being aimed at. An empty list
 * is an answer: the template landed where nobody was standing.
 */
function landShot(ctx: AttackContext, shot: Shot): void {
  const area = ctx.resolveVictims?.(shot.slot, shot.tabId, shot.scatter ?? null, shot.rolledArea) ?? null;
  if (area && area.length === 0) return catchesNobody(ctx, shot);
  const victims = scatterSuppression(
    ctx,
    shot,
    area || [{ tabId: shot.tabId, target: shot.tab.target, rangeBand: ctx.rangeBand }],
  );
  reachNotes(ctx, shot, !!area, victims);
  const free = freeVictims(ctx, shot, victims);
  // Grouped only when there is something to group; a null id keeps a single-target
  // attack byte-identical. A weapon whose victims SPAWN always groups (v8.373.2).
  const spawns = !!(area && shot.rolledArea.spawn && ctx.resolveSpawned);
  const volleyId = free.length > 1 || spawns ? 'volley-' + Date.now() + '-' + shot.tabId : null;
  const caughtIds = strikeAll(ctx, shot, free, { id: volleyId, size: free.length });
  if (spawns) strikeSpawned(ctx, shot, free, caughtIds, volleyId);
}

/** Resume the full hit -> damage -> defence flow from a hit, however it was rolled. */
export function resumeAttackFromHit(ctx: AttackContext, input: HitInput): void {
  landShot(ctx, aimShot(ctx, input));
}
